import type { ColumnDef } from '@tanstack/react-table';
import { MapPinIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { LayerAnalyticsTableRow } from './layerAnalytics.utils';

export type ZoomTarget = Pick<
  LayerAnalyticsTableRow,
  'longitude' | 'latitude' | 'extent'
>;

export type ZoomToTarget = (target: ZoomTarget) => void;

/** Map-icon column that zooms to a row's location; disabled without a map. */
export const buildZoomColumn = <T extends ZoomTarget & { name: string }>({
  subject,
  onZoomToTarget,
  canZoomToTarget,
}: {
  subject: string;
  onZoomToTarget: ZoomToTarget;
  canZoomToTarget: boolean;
}): ColumnDef<T> => ({
  id: 'zoom',
  header: () => <span className="visually-hidden">Zoom to map</span>,
  enableSorting: false,
  cell: ({ row }) => {
    const hasLocation =
      row.original.longitude !== null && row.original.latitude !== null;
    return (
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label={`Zoom to ${row.original.name || subject} on the map`}
        title={
          canZoomToTarget && hasLocation
            ? `Zoom to ${subject}`
            : `Open the map to zoom to this ${subject}`
        }
        disabled={!canZoomToTarget || !hasLocation}
        onClick={() => onZoomToTarget(row.original)}
      >
        <MapPinIcon />
      </Button>
    );
  },
});
